import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { Navigation, Mousewheel, FreeMode } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
const SWIPER_MODULES = [Navigation, Mousewheel, FreeMode];
import { BffBrandType } from '@/types/generated';
import LocationSearchDrawer from './LocationSearchDrawer';
import { groupAppointmentsByDate, getUniqueDates, getFirstNDays, hasMoreDays, buildScheduleUrl, formatFacilityAddress, fetchAppointmentsForFacility } from './SchedulingWidget.helpers';
import { widgetContainerStyles, locationCardStyles, locationInfoStyles, locationNameStyles, locationAddressStyles, locationPhoneStyles, locationDistanceStyles, changeLocationButtonStyles, headingStyles, dayRowStyles, dayHeaderStyles, swiperContainerStyles, timeSlotButtonStyles, buttonGroupStyles, moreTimesButtonStyles, scheduleButtonStyles, loadingContainerStyles, errorContainerStyles, noAppointmentsStyles } from './SchedulingWidget.styles';
import { SchedulingWidgetProps, AppointmentSlot, GroupedAppointments, FacilitySearchResult } from './SchedulingWidget.types';
import ZipCodePicker from './ZipCodePicker';
import TAGButton from '@shared-components/components/TAGButton';
import { useAppContext } from '@shared-components/context';
import { getBffNearByFacilitiesByLocation } from '@shared-components/services/getBffNearByFacilitiesByLocation';
import { logError } from '@utils/miscUtils';

/**
 * Helper function to map BFF facility data to FacilitySearchResult format
 */
function mapFacilityFromBff(facility: {
  code?: string | null;
  displayName?: string | null;
  name?: string | null;
  address?: {
    address1?: string | null;
    city?: string | null;
    stateCode?: string | null;
    zipCode?: string | null;
  } | null;
  location?: {
    distance?: number | null;
  } | null;
  phoneNumber?: string | null;
} | null): FacilitySearchResult {
  return {
    id: facility?.code ?? '',
    name: facility?.displayName || facility?.name || '',
    address: facility?.address?.address1 ?? '',
    city: facility?.address?.city ?? '',
    state: facility?.address?.stateCode ?? '',
    zipCode: facility?.address?.zipCode ?? '',
    distance: facility?.location?.distance ?? undefined,
    phoneNumber: facility?.phoneNumber ?? undefined
  };
}
const filterFacilities = (response: {
  facilityByLocation?: Array<any> | null;
} | null): Array<any> => {
  return response?.facilityByLocation?.filter(f => f !== null) || [];
};

/**
 * SchedulingWidget component for displaying and selecting appointment times on marketing pages
 *
 * Features:
 * - Attempts to auto-detect user location via geolocation on mount
 * - Displays available appointment times for the nearest facility
 * - Falls back to ZIP code search if geolocation fails
 * - Shows earliest 3 days by default, expandable to 6 days
 * - Redirects to scheduler on second "More Times" click
 * - Pre-fills selected time and facility when scheduling
 */
export default function SchedulingWidget({
  enabled = true,
  facilityCode: _facilityCode,
  // We ignore this prop - always start with geolocation
  scheduleConsultationUrl = '/schedule-a-consultation/'
}: Readonly<SchedulingWidgetProps>) {
  const {
    environment,
    config,
    appName,
    appVersion
  } = useAppContext();
  const [appointments, setAppointments] = useState<GroupedAppointments>({});
  const [displayedDays, setDisplayedDays] = useState(3);
  const [selectedSlot, setSelectedSlot] = useState<AppointmentSlot | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<FacilitySearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreTimesClickCount, setMoreTimesClickCount] = useState(0);
  const [geolocationAttempted, setGeolocationAttempted] = useState(false);
  const [searchResults, setSearchResults] = useState<FacilitySearchResult[]>([]);

  // Only show appointments after user has searched and selected a facility
  const currentFacilityId = selectedFacility?.id;
  const hasLocation = !!currentFacilityId;
  const apiUrl = environment === 'prod' ? config.services.prod.CLEARCHOICE_API_URL : config.services.nonprod.CLEARCHOICE_API_URL;
  const bffGraphqlUrl = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;

  // Attempt geolocation on mount
  useEffect(() => {
    let ignore = false;
    const attemptGeolocation = () => {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        if (!ignore) setGeolocationAttempted(true);
        return;
      }
      const onSuccess = async (position: GeolocationPosition) => {
        // Validate coords exist
        if (!position?.coords || typeof position.coords.latitude !== 'number' || typeof position.coords.longitude !== 'number') {
          if (!ignore) setGeolocationAttempted(true);
          return;
        }
        const {
          latitude,
          longitude
        } = position.coords;
        try {
          if (!bffGraphqlUrl) {
            return;
          }
          const response = await getBffNearByFacilitiesByLocation(BffBrandType.Lovet, latitude, longitude, bffGraphqlUrl, appName || 'shared-components-web', appVersion || '', {
            radiusMiles: 200,
            maxItems: 10,
            fetchAllIfNoResults: false
          });
          if (ignore) return;
          if (!response?.facilityByLocation) {
            return;
          }
          const facilities = filterFacilities(response);
          if (facilities.length > 0) {
            const closestFacility = facilities[0];
            const facility = mapFacilityFromBff(closestFacility);
            const allFacilities = facilities.map(mapFacilityFromBff);
            setSearchResults(allFacilities);
            setSelectedFacility(facility);
          }
        } catch (err) {
          if (!ignore) logError(err instanceof Error ? err.message : String(err));
        } finally {
          if (!ignore) setGeolocationAttempted(true);
        }
      };
      const onError = () => {
        // Silently fail and show zip picker
        if (!ignore) setGeolocationAttempted(true);
      };
      navigator.geolocation.getCurrentPosition(position => void onSuccess(position), onError);
    };
    attemptGeolocation();
    return () => {
      ignore = true;
    };
  }, [bffGraphqlUrl, appName, appVersion]);

  // Fetch appointments when facility is determined
  useEffect(() => {
    const loadAppointments = async () => {
      if (!currentFacilityId) return;
      setIsLoading(true);
      setError(null);
      try {
        if (!apiUrl) {
          setError('API configuration error');
          return;
        }
        const result = await fetchAppointmentsForFacility({
          facilityId: currentFacilityId,
          apiUrl,
          appName: appName || 'shared-components-web'
        });
        if (result.error === null) {
          setAppointments(groupAppointmentsByDate(result.slots));
        } else {
          setError(result.error);
        }
      } catch (err) {
        logError(err instanceof Error ? err.message : String(err));
        setError('Failed to load appointments. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    void loadAppointments();
  }, [currentFacilityId, apiUrl, appName]);
  const displayedAppointments = useMemo(() => {
    return getFirstNDays(appointments, displayedDays);
  }, [appointments, displayedDays]);
  const uniqueDates = useMemo(() => {
    return getUniqueDates(displayedAppointments);
  }, [displayedAppointments]);
  const canShowMoreDays = useMemo(() => {
    return hasMoreDays(appointments, displayedDays);
  }, [appointments, displayedDays]);
  const scheduleUrl = useMemo(() => selectedSlot && currentFacilityId ? buildScheduleUrl(scheduleConsultationUrl, currentFacilityId, selectedSlot) : '#', [selectedSlot, currentFacilityId, scheduleConsultationUrl]);
  const moreTimesUrl = useMemo(() => buildScheduleUrl(scheduleConsultationUrl, currentFacilityId || ''), [scheduleConsultationUrl, currentFacilityId]);

  // Don't render if not enabled (control group)
  if (!enabled) {
    return null;
  }

  // Show loading state while attempting geolocation
  if (!geolocationAttempted) {
    return <Box sx={widgetContainerStyles} data-testid="scheduling-widget-geolocation-loading">
				<Box sx={loadingContainerStyles}>
					<CircularProgress />
				</Box>
			</Box>;
  }

  // Render zip code input if no location found after geolocation attempt
  if (!hasLocation) {
    return <ZipCodePicker scheduleConsultationUrl={scheduleConsultationUrl} brand={BffBrandType.Lovet} onFacilityFound={(facility, allFacilities) => {
      setSearchResults(allFacilities);
      setSelectedFacility(facility);
    }} />;
  }
  const handleSelectSlot = (slot: AppointmentSlot) => {
    setSelectedSlot(slot);
  };
  return <Box sx={widgetContainerStyles} data-testid="scheduling-widget" data-sentry-element="Box" data-sentry-component="SchedulingWidget" data-sentry-source-file="SchedulingWidget.tsx">
			<Typography sx={headingStyles} data-sentry-element="Typography" data-sentry-source-file="SchedulingWidget.tsx">Schedule a consultation</Typography>

			{/* Location Card */}
			<Box sx={locationCardStyles} data-sentry-element="Box" data-sentry-source-file="SchedulingWidget.tsx">
				{/* Top row: Name and Distance */}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{
        mb: 0.75
      }} data-sentry-element="Stack" data-sentry-source-file="SchedulingWidget.tsx">
					<Typography variant="bodyMediumSemiBold" sx={locationNameStyles} data-sentry-element="Typography" data-sentry-source-file="SchedulingWidget.tsx">
						ClearChoice {selectedFacility?.city}
					</Typography>
					{selectedFacility?.distance && <Typography variant="bodySmallBook" sx={locationDistanceStyles}>
							{selectedFacility.distance.toFixed(0)} mi
						</Typography>}
				</Stack>

				{/* Bottom section: Address/Phone and Change Location button */}
				<Stack sx={locationInfoStyles} data-sentry-element="Stack" data-sentry-source-file="SchedulingWidget.tsx">
					<Box data-sentry-element="Box" data-sentry-source-file="SchedulingWidget.tsx">
						{selectedFacility && <>
								<Typography component="a" href={`https://maps.google.com/?q=${encodeURIComponent(formatFacilityAddress(selectedFacility))}`} target="_blank" rel="noopener noreferrer" variant="bodySmallBook" sx={locationAddressStyles}>
									{formatFacilityAddress(selectedFacility)}
								</Typography>
								{selectedFacility.phoneNumber && <Typography component="a" href={`tel:${selectedFacility.phoneNumber}`} variant="bodySmallBook" sx={locationPhoneStyles}>
										{selectedFacility.phoneNumber}
									</Typography>}
							</>}
					</Box>
					<TAGButton variant="primaryHC" size="S" onClick={() => setDrawerOpen(true)} sx={changeLocationButtonStyles} dataTestId="change-location-button" data-sentry-element="TAGButton" data-sentry-source-file="SchedulingWidget.tsx">
						Change location
					</TAGButton>
				</Stack>
			</Box>

			{/* Error State */}
			{error && <Box sx={errorContainerStyles}>
					<Typography>{error}</Typography>
				</Box>}

			{/* Loading State */}
			{isLoading && <Box sx={loadingContainerStyles}>
					<CircularProgress />
				</Box>}

			{/* Appointments Display - Each day as a row */}
			{!isLoading && !error && uniqueDates.length > 0 && <>
					{uniqueDates.map(date => {
        const slots = displayedAppointments[date];
        const navigationPrevClass = `swiper-button-prev-${date}`;
        const navigationNextClass = `swiper-button-next-${date}`;
        return <Box key={date} sx={dayRowStyles}>
								<Typography sx={dayHeaderStyles}>{slots[0]?.displayDate}</Typography>
								<Box position="relative" sx={{
            width: '100%'
          }}>
									<Box sx={swiperContainerStyles}>
										<Swiper modules={SWIPER_MODULES} slidesPerView="auto" spaceBetween={16} speed={300} grabCursor simulateTouch touchStartPreventDefault={false} freeMode={{
                enabled: true,
                momentum: true,
                momentumRatio: 0.5
              }} mousewheel={{
                forceToAxis: true
              }} navigation={{
                prevEl: `.${navigationPrevClass}`,
                nextEl: `.${navigationNextClass}`
              }} style={{
                width: '100%'
              }}>
											{slots.map(slot => <SwiperSlide key={`${slot.date}-${slot.time}`} style={{
                  width: 'auto'
                }}>
													<Box component="div" role="button" tabIndex={0} sx={timeSlotButtonStyles(selectedSlot?.date === slot.date && selectedSlot?.time === slot.time)} onClick={() => handleSelectSlot(slot)} onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectSlot(slot);
                    }
                  }} data-testid={`time-slot-${slot.date}-${slot.time}`}>
														{selectedSlot?.date === slot.date && selectedSlot?.time === slot.time && <CheckCircleIcon sx={{
                      color: '#075E86',
                      width: '16px',
                      height: '16px',
                      position: 'absolute',
                      top: '2px',
                      right: '2px'
                    }} />}
														{slot.displayTime}
													</Box>
												</SwiperSlide>)}
										</Swiper>
										<div className={navigationPrevClass} />
										<div className={navigationNextClass} />
									</Box>
								</Box>
							</Box>;
      })}

					{/* Buttons Group */}
					<Box sx={buttonGroupStyles}>
						<TAGButton variant="primaryDefault" href={scheduleUrl} onClick={e => {
          if (!selectedSlot) e.preventDefault();
        }} disabled={!selectedSlot} sx={scheduleButtonStyles(!selectedSlot)} dataTestId="schedule-button">
							Schedule
						</TAGButton>
						{(canShowMoreDays || moreTimesClickCount > 0) && <TAGButton variant="secondaryDefault" href={moreTimesClickCount > 0 ? moreTimesUrl : '#'} onClick={e => {
          if (moreTimesClickCount === 0) {
            e.preventDefault();
            setDisplayedDays(6);
            setMoreTimesClickCount(1);
          }
        }} sx={moreTimesButtonStyles} dataTestId="more-times-button">
								More Times
							</TAGButton>}
					</Box>
				</>}

			{/* No Appointments */}
			{!isLoading && !error && uniqueDates.length === 0 && <Box sx={noAppointmentsStyles}>
					<Typography>No appointments available at this time.</Typography>
					<TAGButton variant="primaryDefault" href={buildScheduleUrl(scheduleConsultationUrl, currentFacilityId)} sx={{
        marginTop: '1rem'
      }} dataTestId="view-all-times-button">
						View All Times
					</TAGButton>
				</Box>}

			{/* Location Search Drawer */}
			<LocationSearchDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onSelectFacility={setSelectedFacility} currentFacilityId={currentFacilityId} initialFacilities={searchResults} brand={BffBrandType.Lovet} data-sentry-element="LocationSearchDrawer" data-sentry-source-file="SchedulingWidget.tsx" />
		</Box>;
}